import React, { Fragment, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { lazyWithRetry } from "../../core/lazy_with_retry";
import { Partner } from "../../data/catalog";
import { useAnalytics } from "../../lib/analytics/analytics";
import {
  useAllLocationsQuery,
  usePartnerCategoryQuery,
  useTestimonialsQuery,
} from "../../offering/api";
import { CatalogCategory } from "./catalog_category";
import { buildPathWithParams, useQueryString } from "@/hooks";
import { DiscountPopupProvider } from "@/core/discount_popup_provider";
interface CatalogCategoryPageParams {
  categorySlug: string;
}

interface CatalogCategoryPageSearchParams {
  promoCode: string;
}

export function CatalogCategoryPage() {
  const pageParams = useParams<CatalogCategoryPageParams>();
  const categorySlug = pageParams.categorySlug;
  const { promoCode: queryPromoCode } =
    useQueryString<CatalogCategoryPageSearchParams>();
  const category = usePartnerCategoryQuery(categorySlug);
  const partner = category?.partnerCatalog?.partner;
  const [partnerLocal, setPartnerLocal] = useState<any>();
  const history = useHistory();

  const Theme = partner
    ? lazyWithRetry(
        () =>
          import(
            `../../offering/${partner.partnerSlug}/${partner.partnerSlug}_theme.tsx`
          )
      )
    : null;
  const Analytics = partner
    ? lazyWithRetry(
        () =>
          import(
            `../../offering/${partner.partnerSlug}/${partner.partnerSlug}_analytics.tsx`
          )
      )
    : null;

  !!partner &&
    import(
      `../../offering/${partner.partnerSlug}/${partner.partnerSlug}_partner_config.tsx`
    ).then((partner) => {
      setPartnerLocal(partner);
    });

  const { data: allLocations } = useAllLocationsQuery(partner?.id);

  const { data: partnerOfferingTestimonials } = useTestimonialsQuery(
    category?.catalogOfferings?.[0]?.partnerOfferingId || ""
  );

  useEffect(() => {
    if (category && category.partnerCatalog && !queryPromoCode) {
      if (category.partnerCatalog.promoCode) {
        const currentUrl = window.location.pathname;
        const updatedUrl = buildPathWithParams(currentUrl, {
          promoCode: category.partnerCatalog.promoCode,
        });
        history.replace(updatedUrl);
      }
    }
  }, [category, history, partner, queryPromoCode]);

  if (
    !category ||
    !category.partnerCatalog?.partner ||
    !partner ||
    !partnerLocal
  ) {
    return null;
  }

  return (
    <DiscountPopupProvider partnerId={partner.id}>
      <Helmet>
        <title>Category Page</title>
        <link
          href={partnerLocal.partnerConfig.favicon}
          rel="shortcut icon"
          type="image/x-icon"
        />
      </Helmet>
      {!!Theme && <Theme />}
      {!!Analytics && <Analytics />}
      <CategoryPageTracker partner={partner} />
      <CatalogCategory
        partnerOfferingTestimonials={partnerOfferingTestimonials}
        allLocations={allLocations}
        partner={partner}
        category={category}
        partnerConfig={partnerLocal.partnerConfig}
      />
    </DiscountPopupProvider>
  );
}

function CategoryPageTracker(props: { partner?: Partner }) {
  const analytics = useAnalytics();
  const { pathname: pathName } = useLocation();
  const { id, internalName, name } = props.partner || {};

  useEffect(() => {
    analytics.screen(
      "Partner Category Page View",
      {
        partnerId: id,
        partnerName: name,
        partnerInternalName: internalName,
        pageName: `Category`,
        Source: "WL",
      },
      { onlyProviders: ["mixpanel", "customerio"] }
    );
  }, [analytics, pathName, id, internalName, name]);

  return <Fragment />;
}
